import { Controller } from "stimulus"

// Empreinte / appareil (WebAuthn) :
// - mode "authenticate" : connexion a la place du code (page de verification)
// - mode "register"     : ajout d'un appareil (page Double authentification)
// Le serveur fournit les options et verifie la reponse ; ici on ne fait que
// convertir les donnees binaires (base64url <-> ArrayBuffer) et appeler le navigateur.
export default class extends Controller {
  static targets = ["button", "message", "nickname"]
  static values = { optionsUrl: String, submitUrl: String, mode: String }

  connect() {
    // Navigateur sans WebAuthn : on masque tout le bloc.
    if (!window.PublicKeyCredential) this.element.hidden = true
  }

  async start(event) {
    event.preventDefault()
    this.setBusy(true)
    this.showMessage("")

    try {
      if (this.modeValue === "register") {
        await this.register()
      } else {
        await this.authenticate()
      }
    } catch (error) {
      this.showMessage(this.errorMessage(error))
      this.setBusy(false)
    }
  }

  async authenticate() {
    const options = await this.request(this.optionsUrlValue, { method: "GET" })

    const publicKey = {
      ...options,
      challenge: this.toBuffer(options.challenge),
      allowCredentials: (options.allowCredentials || []).map((credential) => ({
        ...credential,
        id: this.toBuffer(credential.id)
      }))
    }

    const assertion = await navigator.credentials.get({ publicKey })

    const result = await this.request(this.submitUrlValue, {
      method: "POST",
      body: JSON.stringify({
        credential: {
          id: assertion.id,
          rawId: this.toBase64Url(assertion.rawId),
          type: assertion.type,
          clientExtensionResults: assertion.getClientExtensionResults(),
          response: {
            clientDataJSON: this.toBase64Url(assertion.response.clientDataJSON),
            authenticatorData: this.toBase64Url(assertion.response.authenticatorData),
            signature: this.toBase64Url(assertion.response.signature),
            userHandle: assertion.response.userHandle ? this.toBase64Url(assertion.response.userHandle) : null
          }
        }
      })
    })

    window.location.assign(result.redirect_to)
  }

  async register() {
    const options = await this.request(this.optionsUrlValue, { method: "POST" })

    const publicKey = {
      ...options,
      challenge: this.toBuffer(options.challenge),
      user: { ...options.user, id: this.toBuffer(options.user.id) },
      excludeCredentials: (options.excludeCredentials || []).map((credential) => ({
        ...credential,
        id: this.toBuffer(credential.id)
      }))
    }

    const created = await navigator.credentials.create({ publicKey })

    const result = await this.request(this.submitUrlValue, {
      method: "POST",
      body: JSON.stringify({
        nickname: this.hasNicknameTarget ? this.nicknameTarget.value : "",
        credential: {
          id: created.id,
          rawId: this.toBase64Url(created.rawId),
          type: created.type,
          clientExtensionResults: created.getClientExtensionResults(),
          response: {
            clientDataJSON: this.toBase64Url(created.response.clientDataJSON),
            attestationObject: this.toBase64Url(created.response.attestationObject)
          }
        }
      })
    })

    window.location.assign(result.redirect_to)
  }

  async request(url, { method, body }) {
    const token = document.querySelector("meta[name='csrf-token']")
    const response = await fetch(url, {
      method,
      body,
      credentials: "same-origin",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": token ? token.content : ""
      }
    })

    const data = await response.json().catch(() => ({}))

    if (!response.ok) {
      // Session de connexion expiree : retour a la connexion.
      if (data.redirect_to) window.location.assign(data.redirect_to)
      throw new Error(data.error || "Une erreur s'est produite.")
    }

    return data
  }

  errorMessage(error) {
    if (error && error.name === "NotAllowedError") return "Opération annulée ou délai dépassé."
    if (error && error.name === "InvalidStateError") return "Cet appareil est déjà enregistré."
    if (error && error.name === "SecurityError") return "Connexion sécurisée (HTTPS) requise."
    return (error && error.message) || "Une erreur s'est produite."
  }

  setBusy(busy) {
    if (this.hasButtonTarget) this.buttonTarget.disabled = busy
  }

  showMessage(text) {
    if (!this.hasMessageTarget) return
    this.messageTarget.textContent = text
    this.messageTarget.hidden = text === ""
  }

  toBuffer(value) {
    const base64 = value.replace(/-/g, "+").replace(/_/g, "/")
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4)
    const binary = atob(padded)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    return bytes.buffer
  }

  toBase64Url(buffer) {
    const bytes = new Uint8Array(buffer)
    let binary = ""
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
    return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
  }
}
